import { computed, type ComputedRef, onMounted, watch } from 'vue'

import type {
  Permission,
  Permissions,
} from '@/types/external/2work/api/response/getDoctypePermissions'

import { type DocType, DOCTYPES } from '@/constants/doctypes'
import { PERMISSIONS } from '@/constants/permissions'
import { RelationGroupType, RelationType } from '@/constants/relations'
import { useAuthStore } from '@/stores/auth'

interface PermissionRequirement {
  doctype: DocType
  permissions: Permission[]
}

interface PermissionContext {
  relationGroupType: 'all' | RelationGroupType
  organizationRelationType?: string | undefined
}

interface PermissionStore {
  hasPermissions(doctype: DocType, permissions: Permission[]): boolean
}

interface UseCreateRelationPermissionsOptions {
  getOrganizationRelationType?: (() => string | undefined) | undefined
}

const createRelationPermissionDebugLogKeys = {
  allRelations: 'create-relation-all-relations',
} as const

const lastLoggedCreateRelationPermissionSignatures = new Map<string, string>()
let createRelationPermissionsInitializationPromise: null | Promise<void> = null

const ORGANIZATION_RELATION_TYPE_TO_DOCTYPE: Partial<Record<RelationType, DocType>> = {
  [RelationType.customers]: DOCTYPES.CUSTOMER,
  [RelationType.investors]: DOCTYPES.BANK,
  [RelationType.other]: DOCTYPES.TW_OTHER,
  [RelationType.partners]: DOCTYPES.TW_PARTNER,
  [RelationType.salesPartners]: DOCTYPES.SALES_PARTNER,
  [RelationType.suppliers]: DOCTYPES.SUPPLIER,
}

const SHARED_RELATION_REQUIREMENTS: PermissionRequirement[] = [
  { doctype: DOCTYPES.ADDRESS, permissions: [PERMISSIONS.CREATE] },
  { doctype: DOCTYPES.COUNTRY, permissions: [PERMISSIONS.READ] },
  { doctype: DOCTYPES.TAG, permissions: [PERMISSIONS.READ, PERMISSIONS.CREATE] },
  { doctype: DOCTYPES.TAG_LINK, permissions: [PERMISSIONS.CREATE] },
  { doctype: DOCTYPES.TW_PHONE_TYPE, permissions: [PERMISSIONS.READ] },
  { doctype: DOCTYPES.TW_RATING, permissions: [PERMISSIONS.READ] },
  { doctype: DOCTYPES.TW_WEBLINK_TYPE, permissions: [PERMISSIONS.READ] },
]

const ALL_ORGANIZATION_CREATE_REQUIREMENTS: PermissionRequirement[] = [
  { doctype: DOCTYPES.BANK, permissions: [PERMISSIONS.READ, PERMISSIONS.CREATE] },
  { doctype: DOCTYPES.CUSTOMER, permissions: [PERMISSIONS.READ, PERMISSIONS.CREATE] },
  { doctype: DOCTYPES.SALES_PARTNER, permissions: [PERMISSIONS.READ, PERMISSIONS.CREATE] },
  { doctype: DOCTYPES.SUPPLIER, permissions: [PERMISSIONS.READ, PERMISSIONS.CREATE] },
  { doctype: DOCTYPES.TW_OTHER, permissions: [PERMISSIONS.READ, PERMISSIONS.CREATE] },
  { doctype: DOCTYPES.TW_PARTNER, permissions: [PERMISSIONS.READ, PERMISSIONS.CREATE] },
]

const ORGANIZATION_FORM_REQUIREMENTS: PermissionRequirement[] = [
  { doctype: DOCTYPES.BANK, permissions: [PERMISSIONS.READ] },
  { doctype: DOCTYPES.INDUSTRY_TYPE, permissions: [PERMISSIONS.READ] },
  { doctype: DOCTYPES.TW_INVESTOR_TYPE, permissions: [PERMISSIONS.READ] },
  { doctype: DOCTYPES.TW_OTHER_TYPE, permissions: [PERMISSIONS.READ] },
]

const PERSON_FORM_REQUIREMENTS: PermissionRequirement[] = [
  { doctype: DOCTYPES.CONTACT, permissions: [PERMISSIONS.READ, PERMISSIONS.CREATE] },
  { doctype: DOCTYPES.TW_ROLE, permissions: [PERMISSIONS.READ] },
]

const mergeRequirements = (requirements: PermissionRequirement[]): PermissionRequirement[] => {
  const mergedRequirements = new Map<DocType, Set<Permission>>()

  requirements.forEach(({ doctype, permissions }) => {
    const currentPermissions = mergedRequirements.get(doctype) ?? new Set<Permission>()

    permissions.forEach((permission) => {
      currentPermissions.add(permission)
    })

    mergedRequirements.set(doctype, currentPermissions)
  })

  return Array.from(mergedRequirements.entries()).map(([doctype, permissions]) => {
    return {
      doctype,
      permissions: Array.from(permissions),
    }
  })
}

const resolveOrganizationRelationRequirements = (
  organizationRelationType?: string,
): PermissionRequirement[] => {
  if (!organizationRelationType) {
    return ALL_ORGANIZATION_CREATE_REQUIREMENTS
  }

  const matchingDoctype = Object.entries(ORGANIZATION_RELATION_TYPE_TO_DOCTYPE).find(
    ([relationType]) => {
      return relationType === organizationRelationType
    },
  )?.[1]

  const matchingRequirement = ALL_ORGANIZATION_CREATE_REQUIREMENTS.find((requirement) => {
    return matchingDoctype === requirement.doctype
  })

  if (!matchingRequirement) {
    return ALL_ORGANIZATION_CREATE_REQUIREMENTS
  }

  return [matchingRequirement]
}

const getCreateRelationPermissionRequirements = (
  context: PermissionContext,
): PermissionRequirement[] => {
  if (context.relationGroupType === 'all') {
    return mergeRequirements([
      ...SHARED_RELATION_REQUIREMENTS,
      ...ALL_ORGANIZATION_CREATE_REQUIREMENTS,
      ...ORGANIZATION_FORM_REQUIREMENTS,
      ...PERSON_FORM_REQUIREMENTS,
    ])
  }

  if (context.relationGroupType === RelationGroupType.organizations) {
    return mergeRequirements([
      ...SHARED_RELATION_REQUIREMENTS,
      ...resolveOrganizationRelationRequirements(context.organizationRelationType),
      ...ORGANIZATION_FORM_REQUIREMENTS,
    ])
  }

  return mergeRequirements([
    ...SHARED_RELATION_REQUIREMENTS,
    ...ALL_ORGANIZATION_CREATE_REQUIREMENTS,
    ...PERSON_FORM_REQUIREMENTS,
  ])
}

const getMissingCreateRelationPermissionRequirements = (
  permissionStore: PermissionStore,
  context: PermissionContext,
): PermissionRequirement[] => {
  return getCreateRelationPermissionRequirements(context).filter((requirement) => {
    return !permissionStore.hasPermissions(requirement.doctype, requirement.permissions)
  })
}

const hasCreateRelationPermissions = (
  permissionStore: PermissionStore,
  context: PermissionContext,
): boolean => {
  return getMissingCreateRelationPermissionRequirements(permissionStore, context).length === 0
}

const getMissingCreateRelationDoctypes = (
  permissionStore: PermissionStore,
  context: PermissionContext,
): DocType[] => {
  return getMissingCreateRelationPermissionRequirements(permissionStore, context).map(
    (requirement) => requirement.doctype,
  )
}

const useCreateRelationPermissions = (
  options?: UseCreateRelationPermissionsOptions,
): {
  canCreateAnyRelation: ComputedRef<boolean>
  canCreateOrganizationRelation: ComputedRef<boolean>
  canCreatePersonRelation: ComputedRef<boolean>
  missingDoctypesForAllRelations: ComputedRef<DocType[]>
} => {
  const authStore = useAuthStore()

  const ensureCreateRelationPermissionsLoaded = async (): Promise<void> => {
    if (Object.keys(authStore.accessibleDoctypes).length > 0) {
      return
    }

    if (createRelationPermissionsInitializationPromise) {
      await createRelationPermissionsInitializationPromise

      return
    }

    createRelationPermissionsInitializationPromise = (async (): Promise<void> => {
      const hasValidSession = await authStore.isValidUserSession()

      if (!hasValidSession) {
        return
      }

      await authStore.initAccessibleModules()
    })().finally(() => {
      createRelationPermissionsInitializationPromise = null
    })

    await createRelationPermissionsInitializationPromise
  }

  const getOrganizationRelationType = (): string | undefined => {
    return options?.getOrganizationRelationType?.()
  }

  onMounted(async (): Promise<void> => {
    await ensureCreateRelationPermissionsLoaded()
  })

  const canCreateAnyRelation = computed(() => {
    return hasCreateRelationPermissions(authStore, { relationGroupType: 'all' })
  })

  const canCreateOrganizationRelation = computed(() => {
    return hasCreateRelationPermissions(authStore, {
      organizationRelationType: getOrganizationRelationType(),
      relationGroupType: RelationGroupType.organizations,
    })
  })

  const canCreatePersonRelation = computed(() => {
    return hasCreateRelationPermissions(authStore, {
      relationGroupType: RelationGroupType.persons,
    })
  })

  const missingRequirementsForAllRelations = computed(() => {
    return getMissingCreateRelationPermissionRequirements(authStore, {
      relationGroupType: 'all',
    })
  })

  const missingDoctypesForAllRelations = computed(() => {
    return getMissingCreateRelationDoctypes(authStore, { relationGroupType: 'all' })
  })

  watch(
    missingRequirementsForAllRelations,
    (missingRequirements) => {
      const debugKey = createRelationPermissionDebugLogKeys.allRelations
      const loadedDoctypesCount = Object.keys(authStore.accessibleDoctypes).length

      if (loadedDoctypesCount === 0) {
        return
      }

      if (missingRequirements.length === 0) {
        lastLoggedCreateRelationPermissionSignatures.delete(debugKey)

        return
      }

      const debugPayload = {
        loadedDoctypesCount,
        missingRequirements: missingRequirements.map((requirement) => {
          const permissionSnapshot: null | Permissions =
            authStore.accessibleDoctypes[requirement.doctype] ?? null

          return {
            doctype: requirement.doctype,
            permissionSnapshot,
            requiredPermissions: requirement.permissions,
          }
        }),
      }

      const signature = JSON.stringify(debugPayload)
      if (lastLoggedCreateRelationPermissionSignatures.get(debugKey) === signature) {
        return
      }

      lastLoggedCreateRelationPermissionSignatures.set(debugKey, signature)
      // eslint-disable-next-line no-console
      console.log(
        '[CreateRelation][Debug] Neue Beziehung ist ausgeblendet, weil diese Rechte fehlen:',
        debugPayload,
      )
    },
    { deep: true, immediate: true },
  )

  return {
    canCreateAnyRelation,
    canCreateOrganizationRelation,
    canCreatePersonRelation,
    missingDoctypesForAllRelations,
  }
}

export {
  getCreateRelationPermissionRequirements,
  getMissingCreateRelationDoctypes,
  getMissingCreateRelationPermissionRequirements,
  hasCreateRelationPermissions,
  useCreateRelationPermissions,
}

export type { PermissionContext, PermissionRequirement }
